import { filterChips, FilterState } from 'components/tables/list-table/filter-bar/filterChips';
import posthog from 'posthog-js';
import { isTourMode } from 'tour/tourMode';
import { ActiveExport, claimExportSurface, exportFormatOf } from 'types/exports';
import { isPostHogEnabled } from './posthog';

/**
 * List events. A list goes out by its `surface`, an id its code gives it (the one it keeps its columns under, where it
 * has one), a filter by its selector and a sort by the key the server sorts on: nothing here carries a filter's value,
 * the search text or a cell.
 */

function capture(event: string, properties: Record<string, unknown>): void {
    // A tour clicks through sample lists, which says nothing about how the user works with their own.
    if (!isPostHogEnabled || isTourMode()) {
        return;
    }
    posthog.capture(event, properties);
}

export interface ListFilteredEvent {
    surface: string;
    /** The filters as the action left them. Only the selectors of the ones in use are sent. */
    filterState: FilterState;
    /** Sent as the key `search` when it is not blank, never as text. */
    searchText: string;
}

/** A filter or search the user applied, cleared or reset, once per action. */
export function captureListFiltered({ surface, filterState, searchText }: ListFilteredEvent): void {
    const keys = filterChips(filterState, String).map(({ field }) => field.filter.selector);
    if (searchText.trim()) {
        keys.push('search');
    }
    capture('list_filtered', { surface, filter_keys: [...new Set(keys)].sort() });
}

export interface ListSortedEvent {
    surface: string;
    /** The list's sort as its URL holds it, `key,direction[,nullHandling]`; empty once the sort is cleared. */
    sort: string;
}

/** A column sort the user picked; clearing the sort sends nothing. */
export function captureListSorted({ surface, sort }: ListSortedEvent): void {
    const [column] = sort.split(',');
    if (!column) {
        return;
    }
    capture('list_sorted', { surface, column });
}

/** A view saved from the list, or duplicated from another, once the server has stored it. */
export function captureListViewSaved(surface: string): void {
    capture('list_view_saved', { surface });
}

export interface ListExportEvent {
    surface: string;
    /** Read for its extension alone, as the export ribbon builds the file. */
    fileName: string;
}

/** An export that finished on the server, so a file is ready. */
export function captureListExported({ surface, fileName }: ListExportEvent): void {
    capture('list_exported', { surface, format: exportFormatOf(fileName) });
}

/** An export the server refused to start, or one that failed on its way. */
export function captureListExportFailed({ surface, fileName }: ListExportEvent): void {
    capture('list_export_failed', { surface, format: exportFormatOf(fileName) });
}

/**
 * How an export the ribbon shows ended, sent by the first tab to claim it, whether it saw the end come or a reload
 * restored the export already ended. Nothing while it runs, nor for an export no list started.
 */
export function captureListExportEnd({
    taskId,
    status,
    surface,
    fileName,
}: Pick<ActiveExport, 'taskId' | 'status' | 'surface' | 'fileName'>): void {
    if (!surface || (status !== 'COMPLETED' && status !== 'FAILED') || !claimExportSurface(taskId)) {
        return;
    }
    (status === 'COMPLETED' ? captureListExported : captureListExportFailed)({ surface, fileName });
}
